import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { escapeHtml, unescapeHtml } from '../../modules/utils'
import { AdditionalTypesAutocomplete } from './additional-types-search'

Locale.getInstance().registerKeySet('fr', 'lvc-multiple-disciplines-field', {
  title: 'Disciplines',
  placeholder: 'Rechercher une discipline',
  choose: 'Choisir',
  add: 'Ajouter',
})

Locale.getInstance().registerKeySet('en', 'lvc-multiple-disciplines-field', {
  title: 'Disciplines',
  placeholder: 'Search for a discipline',
  choose: 'Choose',
  add: 'Ajouter',
})

export interface LvcMultipleDisciplinesField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
}
// almost the same component as lvc-multiple-disciplines-field but with multiple selection
// and also a "other" option
export class LvcMultipleDisciplinesField extends MinzeElement {
  private disciplines: any[] = []
  private selectedIds: string[] = []
  private filter: string = ''
  attrs: Attrs = [
    'title',
    'placeholder',
    ['default-value', ''],
    ['background', 'transparent'],
    ['on-change', ''],
    ['error', false],
  ]

  reactive: Reactive = [['value', '']]

  static observedAttributes = ['title', 'on-change', 'error', 'default-value']

  onBlur(e: Event) {
    this.open = false
    this.rerender()
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value') {
      this.updateDefaultValue()
    }
  }

  updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      const parsedDefaultValue = JSON.parse(decodeURIComponent(this.defaultValue))
      this.selectedIds = parsedDefaultValue.map((k: any) => `${k.id}`)

      // Check for custom disciplines and add them if they are not in the disciplines array
      for (const discipline of parsedDefaultValue) {
        if (typeof discipline.id === 'string') {
          if (
            discipline.id.startsWith('custom-') &&
            !this.disciplines.find(d => d.id === discipline.id)
          ) {
            this.disciplines.splice(this.disciplines.length - 1, 0, {
              id: discipline.id,
              labelFr: discipline.labelFr,
              labelEn: discipline.labelEn,
            })
          }
        }
      }
    }
    this.value = this.defaultValue
    this.rerender()
  }

  async onReady() {
    const autocompleteInstance = AdditionalTypesAutocomplete.instance
    this.disciplines = (await autocompleteInstance.getSuggestions(this.filter)) as any[]
    this.disciplines = this.sortDisciplines(this.disciplines)
    this.disciplines.push({
      id: 'other',
      labelFr: Locale.getKey('lvc-ticketing-services-field.other'),
      labelEn: Locale.getKey('lvc-ticketing-services-field.other'),
    })
    this.rerender()
  }

  // I tried sorting in algolia but since we have labelFr and labelEn, it's not possible to sort them properly
  // based on the current language. So I'm sorting them here.
  sortDisciplines(disciplines: any[]): any[] {
    const language = Locale.language
    return disciplines
      .filter(discipline => {
        return !discipline.labelFr.includes('>') && !discipline.labelEn.includes('>')
      })
      .sort((a, b) => {
        if (language === 'fr') {
          return a.labelFr.localeCompare(b.labelFr)
        } else {
          return a.labelEn.localeCompare(b.labelEn)
        }
      })
  }

  onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onDisciplineClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (!value) {
      return
    }

    // handle adding a custom discipline
    if (value === 'other') {
      const otherInput = this.select('[name="other"]') as HTMLInputElement

      if (otherInput) {
        const otherValue = unescapeHtml(otherInput.value.trim())

        if (otherValue) {
          // Generate a custom ID, prefixed with 'custom-'
          const id = `custom-${otherValue
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9]/g, ' ')
            .split(' ')
            .map((word, index) =>
              index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join('')}`

          // if the item already exists, we don't add it
          if (
            this.disciplines.find(
              discipline =>
                discipline.id === id &&
                (discipline.labelFr.toLowerCase() === otherValue.toLowerCase() ||
                  discipline.labelEn.toLowerCase() === otherValue.toLowerCase())
            )
          ) {
            return
          }

          // Add the custom discipline to the list before the 'other' option
          let otherValueLabel = otherValue.charAt(0).toUpperCase() + otherValue.slice(1)
          this.disciplines.splice(this.disciplines.length - 1, 0, {
            id,
            labelFr: otherValueLabel,
            labelEn: otherValueLabel,
          })

          // Add the custom ID to the selected IDs
          this.selectedIds.push(id)
        }
      }
    } else {
      // Handle normal discipline selection
      let index = this.selectedIds.indexOf(value)

      if (index > -1) {
        this.selectedIds.splice(index, 1)
      } else {
        this.selectedIds.push(value)
      }
    }

    this.serializeValue()
    this.rerender()
  }

  onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  serializeValue() {
    const objects = this.disciplines.filter(
      discipline => this.selectedIds.indexOf(`${discipline.id}`) > -1
    )
    this.value = encodeURIComponent(JSON.stringify(objects))
    Callback.call(this.onChange, objects)
    this.cast('disciplines-field:change', this.value)
  }

  getDisciplinesLabel() {
    const language = Locale.language

    if (this.selectedIds.length === 0) {
      return Locale.getKey('lvc-multiple-disciplines-field.choose')
    }

    let labels = this.disciplines
      .filter(k => this.selectedIds.indexOf(`${k.id}`) > -1)
      .map(k => (language === 'fr' ? k.labelFr : k.labelEn))

    if (labels.length === 1) {
      return labels[0]
    }

    if (labels.length > 1) {
      return labels[0] + ' + ' + (labels.length - 1)
    }
  }

  renderDiscipline = (discipline: any) => {
    const userLanguage = Locale.language

    if (
      this.filter &&
      discipline?.[userLanguage === 'fr' ? 'labelFr' : 'labelEn']
        .toLowerCase()
        .indexOf(this.filter.toLowerCase()) === -1
    ) {
      return ''
    }

    const isNotSelected = this.selectedIds.indexOf(`${discipline.id}`) == -1

    if (discipline.id === 'other') {
      return `
        <lvc-box display="flex" alignitems="center">
          <lvc-input name="other" padding="8px 8px" placeholder="${Locale.getKey('lvc-ticketing-services-field.other')}" value="${escapeHtml(discipline.name)}" on-change="serializeValue" background="transparent"></lvc-input>
          <lvc-button enable-hover-box-shadow="true" name="discipline" padding="10px 20px 10px 20px" height="fit-content" data-id="${discipline.id}">${Locale.getKey('lvc-multiple-disciplines-field.add')}</lvc-button>
        </lvc-box>
      `
    }

    return `
      <button 
        name="discipline"
        class="${isNotSelected ? 'not-selected' : 'selected'}" 
        data-id="${discipline.id}">
        ${userLanguage === 'fr' ? discipline.labelFr : discipline.labelEn}
        ${isNotSelected ? '' : `<lvc-circle-check-select-icon data-id="${discipline.id}"></lvc-circle-check-select-icon>`}
      </button>
    `
  }

  renderDisciplines = () => {
    let html = ''

    for (let i = 0; i < this.disciplines.length; i++) {
      html += this.renderDiscipline(this.disciplines[i])
    }

    return html
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0

    return `
      <div class="title">${this.title || Locale.getKey('lvc-multiple-disciplines-field.title')}</div>
      <div class="input ${this.selectedIds?.length ? 'has-value' : ''}">
        <button class="dropbtn" name="toggle">
          ${this.getDisciplinesLabel()}
          <lvc-chevron-down-icon></lvc-chevron-down-icon>
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
            <input name="filter" type="text" value="${escapeHtml(this.filter)}" placeholder="${this.placeholder || Locale.getKey('lvc-multiple-disciplines-field.placeholder')}">
            <lvc-chevron-up-icon></lvc-chevron-up-icon>
          </div>
          <div class="disciplines" data-selected="${this.selectedIds.length}">
            ${this.renderDisciplines()}
          </div>
        </div>
       
      </div> 
      <lvc-box>
        ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
      </lvc-box>
    `
  }

  css = () => {
    return `

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 16px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 16px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        font-family: inherit;
        color: #1E007D;
        font-size: 14px;
        line-height: 17px;
        font-weight: 700;
        margin-bottom: 8px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 24px;
        background: ${this.background ? this.background : 'transparent'};
      }
    

      .disciplines {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .disciplines button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .disciplines button:last-child {
        border-bottom: none;
      }

      .disciplines button.selected {
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 12px 16px;
        font-size: 16px;
        font-weight: 500;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      /* Dropdown Content */
      .dropdown-content {
        width: calc(100% + 2px);
        margin: -1px;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }


    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="discipline"]', 'click', this.onDisciplineClick.bind(this)],
    ['lvc-button[name="discipline"]', 'click', this.onDisciplineClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
